import React from 'react';
import { Icon } from '@iconify-icon/react';

/**
 * Hero visual: a plain-language map of the protected AWS Bedrock path.
 */
const PrivacyFlowDiagram: React.FC = () => (
  <div className="relative mx-auto w-full max-w-md">
    {/* Node: approved knowledge */}
    <div className="relative rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-50">
          <Icon icon="tabler:books" className="text-xl text-[#004790]" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-gray-900">Your approved knowledge</div>
          <div className="truncate text-xs text-gray-500">
            Documents, websites, policies &amp; data
          </div>
        </div>
        <span className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-full bg-gray-100 px-2 py-1 text-[10px] font-semibold text-gray-600">
          <Icon icon="tabler:lock" className="text-xs" />
          Encrypted
        </span>
      </div>
    </div>

    {/* Connector: down */}
    <div className="mx-auto flex h-8 w-px flex-col items-center">
      <svg className="h-full w-px overflow-visible" aria-hidden="true">
        <line
          x1="0.5"
          y1="0"
          x2="0.5"
          y2="100%"
          stroke="#004790"
          strokeWidth="1.5"
          strokeDasharray="4 4"
          className="privacy-flow-dash"
        />
      </svg>
    </div>

    {/* Node: Chat.co controls */}
    <div className="privacy-vault-glow relative rounded-2xl border-2 border-[#04182e] bg-[#04182e] p-5 text-white shadow-xl">
      <div className="flex items-center justify-between gap-2">
        <div className="inline-flex items-center gap-2">
          <Icon icon="tabler:shield-lock" className="text-xl text-[#d3a641]" />
          <span className="text-sm font-bold">Chat.co governance layer</span>
        </div>
        <span className="rounded-full border border-[#d3a641]/40 bg-[#d3a641]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#d3a641]">
          Private
        </span>
      </div>
      <p className="mt-2 text-xs leading-relaxed text-blue-200/70">
        Authenticated access, role-based controls, and encryption keep the right knowledge in the
        right hands.
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        {['Authenticated agents', 'RBAC', 'Encrypted'].map((control) => (
          <span
            key={control}
            className="rounded-full border border-white/10 bg-white/[0.06] px-2.5 py-1 text-[10px] font-semibold text-blue-100">
            {control}
          </span>
        ))}
      </div>
    </div>

    <div className="mx-auto flex h-8 w-px flex-col items-center">
      <svg className="h-full w-px overflow-visible" aria-hidden="true">
        <line
          x1="0.5"
          y1="0"
          x2="0.5"
          y2="100%"
          stroke="#004790"
          strokeWidth="1.5"
          strokeDasharray="4 4"
          className="privacy-flow-dash"
        />
      </svg>
    </div>

    {/* Node: AWS Bedrock */}
    <div className="rounded-2xl border border-blue-200 bg-blue-50/70 p-4">
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white shadow-sm">
          <Icon icon="tabler:cloud-lock" className="text-xl text-[#004790]" />
        </div>
        <div>
          <div className="text-sm font-bold text-gray-900">AWS Bedrock inference</div>
          <p className="mt-0.5 text-[11px] leading-snug text-gray-600">
            Zero data retention by default. No model-provider training on inputs or outputs.
          </p>
        </div>
      </div>
    </div>

    <div className="mx-auto flex h-8 w-px flex-col items-center">
      <svg className="h-full w-px overflow-visible" aria-hidden="true">
        <line
          x1="0.5"
          y1="0"
          x2="0.5"
          y2="100%"
          stroke="#0f9d58"
          strokeWidth="1.5"
          strokeDasharray="4 4"
          className="privacy-flow-dash"
        />
      </svg>
    </div>

    {/* Outcome: grounded answer */}
    <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-4">
      <div className="flex items-center gap-3">
        <Icon icon="tabler:circle-check-filled" className="text-2xl text-emerald-500" />
        <div>
          <div className="text-sm font-bold text-emerald-900">Grounded answer</div>
          <p className="mt-0.5 text-[11px] leading-snug text-emerald-900/70">
            The answer comes back with citations to the approved sources behind it.
          </p>
        </div>
      </div>
    </div>

    <p className="mt-4 text-center font-display text-sm italic text-gray-500">
      Controlled knowledge in. Provable answers out.
    </p>
  </div>
);

export default PrivacyFlowDiagram;
